import React, { useRef, useState, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "./FormularioContacto.css";
import { useTranslation } from "react-i18next";
import { submitContactForm } from "../api";
import { ContactFormData } from "../types";
import Notification from "../../../components/ui/Notification";

const MIN_NOMBRE_LENGTH = 2;
const MIN_EMPRESA_LENGTH = 2;
const MIN_MENSAJE_LENGTH = 10;
const MAX_PHONE_LENGTH = 20;

interface Errors {
  name?: string;
  email?: string;
  companyName?: string;
  message?: string;
  [key: string]: string | undefined;
}

interface Touched {
  name?: boolean;
  email?: boolean;
  companyName?: boolean;
  message?: boolean;
  [key: string]: boolean | undefined;
}


export default function FormularioContacto() {
  const { t, i18n } = useTranslation();
  const captchaRef = useRef<ReCAPTCHA>(null);

  const [token, setToken] = useState<string | null>(null);
  const [enviado, setEnviado] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [nombreUsuario, setNombreUsuario] = useState("");
  const [phoneValue, setPhoneValue] = useState<string>("");
  const [isMobile, setIsMobile] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    companyName: "",
    message: "",
    phone: "",
  });
  const [errors, setErrors] = useState<Errors>({});
  const [touched, setTouched] = useState<Touched>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  const isEmailValid = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  useEffect(() => {
    setIsMobile(window.innerWidth < 480);
  }, []);

  const phoneError = () => {
    if (!phoneValue || phoneValue.trim() === "")
      return t("contacto_form.error_telefono");
    if (!isValidPhoneNumber(phoneValue))
      return t("contacto_form.error_telefono_formato");
    return "";
  };

  const validateField = (name: string, value: string) => {
    let error = "";
    switch (name) {
      case "name":
        if (value.trim().length < MIN_NOMBRE_LENGTH)
          error = t("contacto_form.error_nombre");
        break;
      case "email":
        if (!isEmailValid(value)) error = t("contacto_form.error_email");
        break;
      case "companyName":
        if (value.trim().length < MIN_EMPRESA_LENGTH)
          error = t("contacto_form.error_empresa");
        break;
      case "message":
        if (value.trim().length < MIN_MENSAJE_LENGTH)
          error = t("contacto_form.error_mensaje");
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  const onChange = (value: string | null) => {
    setToken(value);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    let filteredValue = value;

    if (name === "name") {
      filteredValue = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, "");
    }

    setFormData((prev) => ({ ...prev, [name]: filteredValue }));

    if (touched[name]) {
      validateField(name, filteredValue);
    }
  };

  const isFormValid = () => {
    const phoneErr = phoneError();
    return (
      formData.name.trim().length >= MIN_NOMBRE_LENGTH &&
      isEmailValid(formData.email) &&
      formData.message.trim().length >= MIN_MENSAJE_LENGTH &&
      !phoneErr &&
      formData.companyName.trim().length >= MIN_EMPRESA_LENGTH &&
      (recaptchaSiteKey ? token !== null : true) &&
      Object.values(errors).every((err) => !err)
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const currentErrors: Errors = {};
    if (formData.name.trim().length < MIN_NOMBRE_LENGTH)
      currentErrors.name = t("contacto_form.error_nombre");
    if (!isEmailValid(formData.email))
      currentErrors.email = t("contacto_form.error_email");
    if (formData.message.trim().length < MIN_MENSAJE_LENGTH)
      currentErrors.message = t("contacto_form.error_mensaje");
    if (formData.companyName.trim().length < MIN_EMPRESA_LENGTH)
      currentErrors.companyName = t("contacto_form.error_empresa");

    const phoneErr = phoneError();
    if (phoneErr) {
      setPhoneTouched(true);
    }

    if (Object.keys(currentErrors).length > 0 || phoneErr) {
      setErrors(currentErrors);
      setTouched(
        Object.keys(formData).reduce(
          (acc, key) => ({ ...acc, [key]: true }),
          {},
        ),
      );
      return;
    }

    if (recaptchaSiteKey && !token) {
      setSubmitError(t("contacto_form.captcha_required"));
      return;
    }

    setCargando(true);
    setSubmitError(null);

    const cleanData = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      companyName: formData.companyName.trim(),
      phone: phoneValue || "",
      message: formData.message.trim(),
    };

    setNombreUsuario(cleanData.name);

    try {
      await submitContactForm(cleanData, token);
      setSuccessMessage(t("contacto_form.exito", { nombre: cleanData.name }));
      setEnviado(true);
    } catch (error: any) {
      const baseMessage = error.friendlyMessage || t("contacto_form.error");
      setSubmitError(baseMessage);
      setToken(null);
      if (captchaRef.current) captchaRef.current.reset();
    } finally {
      setCargando(false);
    }
  };

  if (enviado) {
    return (
      <div className="footer-form-contacto">
        <Notification 
          message={successMessage} 
          type="success" 
          onClose={() => setSuccessMessage(null)} 
        />
        <h3 className="footer-form-contacto-title">
          {t("contacto_form.exito", { nombre: nombreUsuario })}
        </h3>
      </div>
    );
  }

  const currentPhoneError = phoneTouched ? phoneError() : "";

  return (
    <div className="footer-form-contacto">
      <h3 className="footer-form-contacto-title">
        {t("contacto_form.titulo")}
      </h3>

      <form onSubmit={handleSubmit}>
        {submitError && (
          <div className="form-submit-error" role="alert">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span>{submitError}</span>
          </div>
        )}
        <input
          name="name"
          type="text"
          className={touched.name && errors.name ? "invalid" : ""}
          placeholder={t("contacto_form.placeholder_nombre")}
          value={formData.name}
          onChange={handleChange}
          onBlur={handleBlur}
          maxLength={100}
          required
          disabled={cargando}
        />
        {touched.name && errors.name && (
          <p className="error-message">{errors.name}</p>
        )}

        <input
          name="email"
          type="email"
          className={touched.email && errors.email ? "invalid" : ""}
          placeholder={t("contacto_form.placeholder_email")}
          value={formData.email}
          onChange={handleChange}
          onBlur={handleBlur}
          maxLength={100}
          required
          disabled={cargando}
        />
        {touched.email && errors.email && (
          <p className="error-message">{errors.email}</p>
        )}

        <input
          name="companyName"
          type="text"
          className={touched.companyName && errors.companyName ? "invalid" : ""}
          placeholder={t("contacto_form.placeholder_empresa")}
          value={formData.companyName}
          onChange={handleChange}
          onBlur={handleBlur}
          maxLength={100}
          required
          disabled={cargando}
        />
        {touched.companyName && errors.companyName && (
          <p className="error-message">{errors.companyName}</p>
        )}

        <div
          className={`phone-field-wrapper${currentPhoneError ? " phone-invalid" : ""}`}
        >
          <PhoneInput
            international
            defaultCountry="AR"
            value={phoneValue}
            onChange={(val) => {
              setPhoneValue(val || "");
            }}
            onBlur={() => setPhoneTouched(true)}
            disabled={cargando}
            placeholder={t("contacto_form.placeholder_telefono")}
            maxLength={MAX_PHONE_LENGTH}
          />
        </div>
        {currentPhoneError && (
          <p className="error-message">{currentPhoneError}</p>
        )}

        <textarea
          name="message"
          className={touched.message && errors.message ? "invalid" : ""}
          placeholder={t("contacto_form.placeholder_mensaje")}
          value={formData.message}
          onChange={handleChange}
          onBlur={handleBlur}
          maxLength={1000}
          rows={3}
          required
          disabled={cargando}
        />
        {touched.message && errors.message && (
          <p className="error-message">{errors.message}</p>
        )}

        <div
          className="recaptcha-container"
          style={{ marginBottom: "15px" } as React.CSSProperties}
        >
          <div className="recaptcha-wrapper">
            {recaptchaSiteKey ? (
              <ReCAPTCHA
                size={isMobile ? "compact" : "normal"}
                key={i18n.language}
                ref={captchaRef}
                hl={i18n.language}
                sitekey={recaptchaSiteKey}
                onChange={onChange}
              />
            ) : (
              <p className="error-message" style={{ margin: 0 }}>
                {t("contacto_form.captcha_not_configured")}
              </p>
            )}
          </div>
        </div>


        <button
          type="submit"
          disabled={!isFormValid() || cargando}
          style={
            {
              cursor: cargando
                ? "wait"
                : !isFormValid()
                  ? "not-allowed"
                  : "pointer",
            } as React.CSSProperties
          }
        >
          {cargando ? t("contacto_form.enviando") : t("contacto_form.boton")}
        </button>
      </form>
    </div>
  );
}
